import React, { createContext, useContext, useEffect, useState } from "react";
import { api, describeAxiosError } from "@/lib/api";
import { hasPermission, hasAnyPermission, hasAllPermissions } from "@/lib/permissions";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = user
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Prefer permissions embedded in /auth/me (single round-trip). Fall back to
      // the dedicated endpoint for older backend builds.
      if (Array.isArray(data?.permissions)) {
        setPermissions(data.permissions);
      } else {
        try {
          const p = await api.get("/users/me/permissions");
          setPermissions(p.data.permissions || []);
        } catch {
          setPermissions([]);
        }
      }
    } catch {
      setUser(false);
      setPermissions([]);
      localStorage.removeItem("access_token");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("access_token", data.access_token);
      setUser(data.user);
      if (Array.isArray(data.user?.permissions)) {
        setPermissions(data.user.permissions);
      } else {
        try {
          const p = await api.get("/users/me/permissions");
          setPermissions(p.data.permissions || []);
        } catch {
          setPermissions([]);
        }
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: describeAxiosError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("access_token");
    setUser(false);
    setPermissions([]);
  };

  // Central permission helpers (single source of truth).
  const can = (perm) => hasPermission(permissions, perm);
  const canAny = (perms) => hasAnyPermission(permissions, perms);
  const canAll = (perms) => hasAllPermissions(permissions, perms);

  return (
    <AuthContext.Provider value={{
      user, permissions, loading,
      can, canAny, canAll,
      // named aliases exposed for readability at call-sites
      hasPermission: can, hasAnyPermission: canAny, hasAllPermissions: canAll,
      login, logout, refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
